import React, { useCallback, useEffect, useState } from 'react';
import { useFullReset, useKioskState } from '../../../context/Kiosk/kiosk';
import { useLocalization } from '../../../context/Localization/localization';
import { Spinner } from '../../../controls/Spinner/Spinner';
import { GMAppointmentName } from '../../../controls/AppointmentName';
import { Spacer } from '../../../controls/Spacer/Spacer';
import { formatAsCurrency } from '../../../helpers/Number';
import { useDisplayLocalizedVehicle, DisplayMake } from '../../../helpers/displayVehicle';
import css from './PaymentLanding.module.scss';
import { useAppointmentStateUnsafe } from '../../../context/Appointment/appointment';
import { CTAButton } from '../../../controls/ButtonBase/index';
import { ScrollableBody } from '../../Layout/ScrollableBody';
import { DealerFooter } from '../Payment/DealerFooter';
import classNames from 'classnames';
import { PaymentProvider } from '../../../API/Enums';
import { GMDialogError } from '../../../controls/Dialog';
import { GMButtonGroup } from '../../../controls/ButtonGroup';

interface IProps {
    onSkip: () => void;
    onProceed: () => void;
    errorMessage: string;
}

export const PaymentLanding: React.FC<IProps> = ({ onSkip, onProceed, errorMessage }) => {
    const appointment = useAppointmentStateUnsafe();
    const Localization = useLocalization();
    const {
        dealer: { onlinePaymentsActive, country, paymentProvider },
    } = useKioskState();
    const reset = useFullReset(true, false);
    const displayLocalizedVehicle = useDisplayLocalizedVehicle();
    const [showPaymentErrorDialog, setShowPaymentErrorDialog] = useState<boolean>(false);
    const [paymentErrorMessage] = useState<string>(errorMessage);

    useEffect(() => {
        if (!appointment) {
            reset();
        }
        else if (appointment.paymentStatus === 'no payment due' || appointment.paymentStatus === 'marked paid') {
            onSkip();
        }
        else if (appointment.paymentStatus === 'paid') {
            /*
             * We don't skip because we need the payment session information (for Stripe) and the payment screens
             * will grab the completed payment session and proceed to the payment status screen
             */
            onProceed();
        }
        else if (!onlinePaymentsActive || paymentProvider === PaymentProvider.None) {
            setShowPaymentErrorDialog(true);
        }
    }, [appointment, onProceed, onSkip, onlinePaymentsActive, paymentProvider, reset]);

    const onPayNow = useCallback(() => {
        onProceed();
    }, [onProceed]);

    return (
        <div className={css.root}>
            <GMDialogError
                ariaLabel={Localization.FailedToEstablishPaymentSession}
                open={showPaymentErrorDialog}
                onClose={reset}
            >
                {Localization.FailedToEstablishPaymentSession}
            </GMDialogError>
            {!appointment || appointment.paymentStatus === 'no payment due' || appointment.paymentStatus === 'marked paid' || appointment.paymentStatus === 'paid' ? (
                <Spinner />
            ) : (
                <>
                    <p>
                        <GMAppointmentName appointment={appointment} />
                    </p>
                    <br />
                    <p>{Localization.PleaseConfirmServiceDetailsAndSelectPayNow}</p>
                    <br />
                    <Spacer />
                    <br />
                    <ScrollableBody
                        hideAccessibilityButton
                        proceedButtons={
                            <GMButtonGroup
                                buttons={[
                                    <CTAButton
                                        onClick={onPayNow}
                                        styleType={'primary'}
                                        label={Localization.PayNow}
                                        ariaLabel={Localization.PayNow}
                                        id={Localization.PayNow}
                                    />,
                                ]}
                            />
                        }
                    >
                        {paymentErrorMessage ?
                            <p className={css.error}>{paymentErrorMessage}</p> :
                            null
                        }

                        <div className={classNames(css.paymentInfo, { [css.hasError]: !!paymentErrorMessage })}>
                            <div className={css.name}>
                                {`${Localization.Name}: `}
                                <b>
                                    <GMAppointmentName
                                        appointment={appointment}
                                        fullName
                                        lastFirst={false}
                                    />
                                </b>
                            </div>
                            <table>
                                <tbody>
                                    <tr>
                                        <td className={css.leftCol} colSpan={2}>
                                            {Localization.RONumber} {appointment.roNumber}
                                        </td>
                                    </tr>
                                    {appointment.year && DisplayMake(appointment) ? (
                                        <tr>
                                            <td className={css.leftCol} colSpan={2}>
                                                {displayLocalizedVehicle(appointment)}
                                            </td>
                                        </tr>
                                    ) : null}
                                    <tr>
                                        <td className={css.leftCol}>
                                            <b>{Localization.PaymentAmount}</b>
                                        </td>
                                        <td className={css.leftCol}>
                                            <b> {formatAsCurrency(appointment.serviceTotal || 0, true, country)}</b>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </ScrollableBody>
                    <DealerFooter />
                </>
            )}
        </div>
    );
};
